const MS_IN_DAY = 86_400_000;
const END_OF_DAY_OFFSET = 86_399_999;

const isNumberLike = (value: any): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

/**
 * Formats a number using fixed-point notation.
 * Returns `N/A` when the input is not a valid number.
 */
export const toFixedNumber = (item: any, num: number): string => {
  if (!isNumberLike(item)) {
    return 'N/A';
  }

  const fixed = item.toFixed(num);
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
};

/**
 * Formats a number using precision notation.
 * Returns `N/A` when the input is not a valid number.
 */
export const toPrecision = (item: any, num: number): string => {
  return isNumberLike(item) ? item.toPrecision(num) : 'N/A';
};

/**
 * Converts milliseconds into `DD day(s) HH:MM:SS` or `HH:MM:SS` format.
 */
export const toHHMMSS = (milliseconds: number, withoutDay?: boolean): string => {
  const sec = Math.floor(milliseconds / 1000);
  const day = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((sec % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (sec % 60).toString().padStart(2, '0');

  return day && !withoutDay
    ? `${day}${day === 1 ? ' day ' : ' days '}${hours}:${minutes}:${seconds}`
    : `${hours}:${minutes}:${seconds}`;
};

export const toHHMMSSRTYesterday = (milliseconds: number): string => {
  /**
   * Converts milliseconds into `HH:MM:SS` capped at `24:00:00`.
   */
  const sec = Math.floor(milliseconds / 1000);
  let hours = Math.floor(sec / 3600);
  let minutes = Math.floor((sec % 3600) / 60);
  let seconds = sec % 60;

  if (hours >= 24) {
    hours = 24;
    minutes = 0;
    seconds = 0;
  }

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0'),
  ].join(':');
};

export const startOfDay = (day: number, offset?: number): number => {
  /**
   * Returns the timestamp for the start of day, with optional timezone offset.
   */
  return day - ((offset ? day - offset : day) % MS_IN_DAY);
};

export const endOfDay = (day: number, offset?: number): number => {
  /**
   * Returns the timestamp for the end of day, with optional timezone offset.
   */
  return startOfDay(day, offset) + END_OF_DAY_OFFSET;
};

/**
 * Generates a pseudo-random numeric identifier.
 */
export default function UID(): number {
  return Math.floor((1 + Math.random()) * 0x10000000000000);
}

/**
 * Truncates a number to a fixed amount of decimal digits (without rounding).
 */
export const truncateDecimals = (num: number, digits: number): number => {
  const factor = 10 ** digits;
  return Math.trunc(num * factor) / factor;
};

/**
 * Formats a timestamp as an abbreviated date, e.g. "Oct. 5, 2026".
 */
export const formatDate = (timePoint: number): string => {
  const date = new Date(timePoint);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  return `${month}. ${date.getDate()}, ${date.getFullYear()}`;
};
